import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

interface SvgLogoProps {
  url: string
  alt: string
  color?: string   // any CSS color; defaults to 'black'
  className?: string
}

export function SvgLogo({ url, alt, color = 'currentColor', className }: SvgLogoProps) {
  const [svgMarkup, setSvgMarkup] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    setSvgMarkup(null)
    setError(false)
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed')
        return res.text()
      })
      .then((text) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(text, 'image/svg+xml')
        const svg = doc.querySelector('svg')
        if (!svg) throw new Error('no svg element')

        // Remove embedded stylesheets — they override attribute-level fills
        svg.querySelectorAll('style, defs').forEach((el) => el.remove())

        // Strip every fill/stroke attribute and inline style from all descendants,
        // except fill="none" (used for cutouts/outlines — preserve those).
        svg.querySelectorAll('*').forEach((el) => {
          const fill = el.getAttribute('fill')
          if (fill !== 'none') el.removeAttribute('fill')
          el.removeAttribute('stroke')
          const htmlEl = el as HTMLElement
          htmlEl.style.removeProperty('fill')
          htmlEl.style.removeProperty('stroke')
          htmlEl.style.removeProperty('color')
        })

        // Set color on the root — all descendants inherit it via SVG fill cascade
        svg.setAttribute('fill', color)

        // Normalize sizing: strip fixed dims, ensure viewBox exists
        const w = svg.getAttribute('width')
        const h = svg.getAttribute('height')
        if (!svg.getAttribute('viewBox') && w && h) {
          svg.setAttribute('viewBox', `0 0 ${parseFloat(w)} ${parseFloat(h)}`)
        }
        svg.removeAttribute('width')
        svg.removeAttribute('height')
        svg.setAttribute('style', 'display:block;height:100%;width:auto;')
        svg.setAttribute('aria-label', alt)
        svg.setAttribute('role', 'img')

        setSvgMarkup(svg.outerHTML)
      })
      .catch(() => setError(true))
  }, [url, color, alt])

  if (error) return null
  if (!svgMarkup) return <div className={cn('animate-pulse rounded bg-muted', className)} />

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: svgMarkup }}
    />
  )
}
